import type { Maybe } from '@adornis/base/utilTypes.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { ValidationFunction } from '@adornis/validation/validator.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { isBackdatedEnd, isBackdatedStart, isNegativeRuntime, type MandateRuntime } from './mandateActivity.js';

/**
 * Form validator for a mandate's `end`: only the administrative SEPA roles may
 * {@link isBackdatedEnd | backdate} it. `previousEnd` is the stored value, so a mandate an
 * administrator already ended in the past stays editable for its owner.
 *
 * `useForm` runs this on every pick, so a Sorgeberechtigter sees the refusal on the field itself
 * rather than after a round trip.
 */
export const mandateEndNotBackdated =
  ({ canBackdate, previousEnd }: { canBackdate: boolean; previousEnd: Maybe<Date> }): ValidationFunction<any, any> =>
  options => {
    if (canBackdate) return;
    if (!isBackdatedEnd(options.value as Maybe<Date>, previousEnd)) return;

    throw new ValidationError(`Ein SEPA-Mandat kann nicht rückwirkend beendet werden.`, {
      key: options.key,
      path: options.path,
    });
  };

/**
 * Form validator for a mandate's `start`: only the administrative SEPA roles may
 * {@link isBackdatedStart | backdate} it. `previousStart` is the stored value.
 */
export const mandateStartNotBackdated =
  ({
    canBackdate,
    previousStart,
  }: {
    canBackdate: boolean;
    previousStart: Maybe<Date>;
  }): ValidationFunction<any, any> =>
  options => {
    if (canBackdate) return;
    if (!isBackdatedStart(options.value as Maybe<Date>, previousStart)) return;

    throw new ValidationError(`Ein SEPA-Mandat kann nicht rückwirkend beginnen.`, {
      key: options.key,
      path: options.path,
    });
  };

/**
 * Form validator for both bounds of a mandate's runtime: it must not be
 * {@link isNegativeRuntime | negative}. It reads the whole runtime off the validated document rather
 * than its own value, so the same function sits on `start` and on `end` and the error shows up on
 * whichever of the two was just picked. `previous` is the stored runtime - see `isNegativeRuntime`.
 */
export const mandateRuntimeNotNegative =
  ({ previous }: { previous: MandateRuntime }): ValidationFunction<any, any> =>
  options => {
    const runtime = options.target as MandateRuntime;
    if (!isNegativeRuntime(runtime, previous)) return;

    throw new ValidationError(
      `Ein SEPA-Mandat kann nicht vor seinem Start (${formatCalendarDay(runtime.start as Date, 'dd.MM.yyyy')}) enden.`,
      { key: options.key, path: options.path },
    );
  };
